import { RadioCardGroup } from "@blurb/codex-react";
import { Box, createStyles, Stack } from "@mantine/core";
import { RpiAlert, RpiText } from "@rpi/openapi-core";
import {
  attributesForFiltering,
  invalidateSubsequentSteps,
  NewProductState,
  ProductCreateNavButtons,
  StepProps,
} from "./ProductCreateNavigator";
import {
  filterByMatchingAttributes,
  findAvailableAttributeValues,
  StandardProduct,
} from "../../hooks/useStandardProducts";
import { ReactNode, useState } from "react";

type ChoiceStepProps = StepProps & {
  instructionText?: ReactNode;
  noOptionsText?: string;
  standardProducts: StandardProduct[];
  flexDirection?: "row" | "column";
  isLoading?: boolean;
  renderCard: (value: string) => JSX.Element | null;
  renderImage?: (value: string) => JSX.Element | null;
  sortValues?: (values: (string | number | boolean)[]) => (string | number | boolean)[];
  getFilterAttributes?: (state: StepProps["state"]) => Record<string, string | number | boolean | undefined>;
  onAdditionalValidation?: () => Promise<ReactNode | undefined>;
};

const useCardStepStyles = createStyles((theme, { flexDirection }: { flexDirection: "row" | "column" }) => ({
  cardGrid: {
    display: "flex",
    gap: "var(--codex-spacing-2)",
    marginTop: "var(--codex-spacing-8)",
    marginBottom: "var(--codex-spacing-2)",
    flexDirection,
    flexWrap: "wrap",
  },
}));

// Generic card step component
export function ChoiceStep({
  state,
  setState,
  attributeKey,
  instructionText = "Please select one to continue.",
  noOptionsText = "No options available based on previous selections.",
  standardProducts,
  renderCard,
  renderImage,
  flexDirection = "row",
  getFilterAttributes,
  sortValues,
  onAdditionalValidation,
  isLoading,
}: ChoiceStepProps) {
  const { classes } = useCardStepStyles({ flexDirection });
  const [errorMessage, setErrorMessage] = useState<ReactNode>("");

  if (!attributeKey) return <RpiAlert>Configuration error: No attribute key.</RpiAlert>;

  // Use custom filter attributes or default to attributesForFiltering
  const filterAttributes = getFilterAttributes ? getFilterAttributes(state) : attributesForFiltering(state);
  const filteredProducts = filterByMatchingAttributes(standardProducts, filterAttributes);
  const displayValues = findAvailableAttributeValues(filteredProducts, attributeKey);
  const sortedValues = sortValues ? sortValues(displayValues) : displayValues;

  const currentValue = state.attributes[attributeKey] ?? "";
  const handleSelect = (value: string | number | boolean | undefined) => {
    setState((draft) => {
      draft.attributes[attributeKey] = value;
      draft.nextFailed = false;
      invalidateSubsequentSteps(draft, draft.currentStepIndex);
    });
  };

  const handleNext = async () => {
    const validationError = validateSelection(attributeKey, state) || (await onAdditionalValidation?.());

    if (validationError) {
      setErrorMessage(validationError);
      setState((draft) => {
        draft.nextFailed = true;
      });
    } else {
      setErrorMessage("");
      setState((draft) => {
        draft.completedStepIndex = draft.currentStepIndex;
        draft.currentStepIndex += 1;
        draft.nextFailed = false;
      });
    }
  };

  return (
    <Stack spacing={0}>
      <RpiText>{instructionText}</RpiText>

      {displayValues.length === 0 ? (
        <RpiAlert>{noOptionsText}</RpiAlert>
      ) : (
        <RadioCardGroup value={currentValue} onValueChange={(value) => handleSelect(value as typeof currentValue)}>
          <Box className={classes.cardGrid}>
            {sortedValues.map((value) => {
              return renderCard(String(value));
            })}
          </Box>
        </RadioCardGroup>
      )}

      <Box sx={{ marginBottom: "var(--codex-spacing-6)" }}>
        {currentValue && renderImage && renderImage(String(currentValue))}
      </Box>
      {state.nextFailed && errorMessage && <RpiAlert>{errorMessage}</RpiAlert>}

      <ProductCreateNavButtons state={state} setState={setState} onNext={handleNext} loading={isLoading} />
    </Stack>
  );
}

const noSelectionErrorText = "Please make a selection to continue.";
function validateSelection(attributeKey: string, state: NewProductState) {
  if (!state.attributes[attributeKey]) {
    return noSelectionErrorText;
  }
}
